import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { CohortData, CohortOptions, CohortSegment, UserCohort } from "@/types/cohort.types";

export const useCohortAnalysis = (options: CohortOptions) => {
  const { startDate, endDate, granularity, segment = 'all' } = options;

  return useQuery({
    queryKey: ['cohort-analysis', startDate, endDate, granularity, segment],
    queryFn: async (): Promise<{
      cohorts: CohortData[];
      segments: CohortSegment[];
    }> => {
      // Fetch all cohort data within date range
      const { data: cohortData, error } = await supabase
        .from('user_cohorts')
        .select('*')
        .gte('cohort_date', startDate)
        .lte('cohort_date', endDate)
        .order('cohort_date', { ascending: false });

      if (error) throw error;

      const cohorts = cohortData as UserCohort[];

      // Group by cohort period
      const cohortMap = new Map<string, UserCohort[]>();
      
      cohorts.forEach(cohort => {
        const key = granularity === 'weekly' 
          ? cohort.cohort_week || cohort.cohort_date
          : cohort.cohort_month || cohort.cohort_date.substring(0, 7);
        
        if (!cohortMap.has(key)) {
          cohortMap.set(key, []);
        }
        cohortMap.get(key)!.push(cohort);
      });

      // Calculate cohort metrics
      const cohortResults: CohortData[] = Array.from(cohortMap.entries()).map(([cohortKey, users]) => {
        const cohortSize = users.length;
        const conversions = users.filter(u => u.converted).length;
        const conversionRate = cohortSize > 0 ? (conversions / cohortSize) * 100 : 0;

        // Calculate retention per period (weeks since cohort start)
        const cohortStartDate = new Date(users[0].cohort_date);
        const retention: { period: number; activeUsers: number; retentionRate: number }[] = [];

        for (let period = 0; period <= 4; period++) {
          const periodEndDate = new Date(cohortStartDate);
          periodEndDate.setDate(periodEndDate.getDate() + (period + 1) * 7);

          const activeInPeriod = users.filter(u => {
            if (!u.created_at) return false;
            const userDate = new Date(u.created_at);
            return userDate <= periodEndDate;
          }).length;

          retention.push({
            period,
            activeUsers: activeInPeriod,
            retentionRate: cohortSize > 0 ? (activeInPeriod / cohortSize) * 100 : 0
          });
        }

        return {
          cohort: cohortKey,
          cohortDate: users[0].cohort_date,
          cohortSize,
          conversions,
          conversionRate,
          retention
        };
      });

      // Calculate segment data if needed
      let segments: CohortSegment[] = [];
      
      if (segment !== 'all') {
        const segmentMap = new Map<string, UserCohort[]>();
        
        cohorts.forEach(cohort => {
          let segmentValue = '';
          if (segment === 'practice_type') segmentValue = cohort.practice_type || 'Unbekannt';
          else if (segment === 'source') segmentValue = cohort.acquisition_source || 'Unbekannt';
          else if (segment === 'device') segmentValue = cohort.device_type || 'Unbekannt';

          if (!segmentMap.has(segmentValue)) {
            segmentMap.set(segmentValue, []);
          }
          segmentMap.get(segmentValue)!.push(cohort);
        });

        segments = Array.from(segmentMap.entries()).map(([name, users]) => {
          const count = users.length;
          const conversions = users.filter(u => u.converted).length;
          const conversionRate = count > 0 ? (conversions / count) * 100 : 0;

          // Calculate avg time to conversion
          const convertedUsers = users.filter(u => u.converted && u.conversion_date);
          const avgTimeToConversion = convertedUsers.length > 0
            ? convertedUsers.reduce((sum, u) => {
                const cohortDate = new Date(u.cohort_date);
                const convDate = new Date(u.conversion_date!);
                return sum + (convDate.getTime() - cohortDate.getTime()) / (1000 * 60 * 60 * 24);
              }, 0) / convertedUsers.length
            : 0;

          // Find top entry page
          const pageCount = new Map<string, number>();
          users.forEach(u => {
            if (u.first_page) {
              pageCount.set(u.first_page, (pageCount.get(u.first_page) || 0) + 1);
            }
          });
          const topEntryPage = Array.from(pageCount.entries())
            .sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A';

          return {
            name,
            value: name,
            count,
            conversionRate,
            avgTimeToConversion,
            topEntryPage
          };
        });

        // Sort by conversion rate descending
        segments.sort((a, b) => b.conversionRate - a.conversionRate);
      }

      return {
        cohorts: cohortResults,
        segments
      };
    },
    refetchInterval: 60000, // Refetch every minute
  });
};
